// Standard Library
// 3rd Party
// Local
import { UriAwareMixin } from './mixins/uri-aware.js';

/**
 * Uri Router Navigation — highlights which route button is active for a
 * fragment. Mirrors uri-router's effectiveValue / default-mode rules so
 * tabs stay correct when nested defaults are lazy (Issue #400).
 *
 * Does not need to persist defaults itself when a peer uri-router on the
 * same fragment runs ensureDefaultPersisted; still implements the same
 * API so standalone use stays consistent.
 */
// eslint-disable-next-line new-cap
class UriRouterNavigationElement extends UriAwareMixin(HTMLElement) {
  /**
   * @returns {string[]}
   */
  static get observedAttributes() {
    return [
      'uri',
      'fragment',
      'data-fragment-name',
      'default',
      'default-mode',
      'active-when-fragment',
      'active-when-value',
    ];
  }

  constructor() {
    super();
    this._onHashChange = this.onHashChange.bind(this);
    this._syncingUri = false;
  }

  /**
   * @param {string} name
   * @param {string|null} oldValue
   * @param {string|null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    const hasValue = newValue !== null;

    if (name === 'uri' && hasValue) {
      if (!this._syncingUri && this.hashMap[this.fragment] !== newValue) {
        this.hashValue = newValue;
      }
      this.activatePage();
    }
  }

  connectedCallback() {
    if (!this.hasAttribute('data-fragment-name')) {
      this.setAttribute('data-fragment-name', 'data-fragment');
    }

    this._boundNavigationClick = this.navigationClick.bind(this);
    window.addEventListener('hashchange', this._onHashChange, false);
    this.ensureDefaultPersisted();
    this.activatePage();
    this._bindRouteListeners();

    this._observer = new MutationObserver(() => {
      this._bindRouteListeners();
      this.activatePage();
    });
    this._observer.observe(this, { childList: true });
  }

  disconnectedCallback() {
    window.removeEventListener('hashchange', this._onHashChange, false);
    this.routes.forEach((c) => {
      c.removeEventListener('click', this._boundNavigationClick);
    });
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }

  _bindRouteListeners() {
    this.routes.forEach((c) => {
      c.removeEventListener('click', this._boundNavigationClick);
      c.addEventListener('click', this._boundNavigationClick);
    });
  }

  get _$Map() {
    return {
      $routes: this.routes,
    };
  }

  set dataFragmentName(newFragmentName) {
    if (newFragmentName !== null) {
      this.setAttribute('data-fragment-name', newFragmentName);
    }
  }

  get dataFragmentName() {
    return this.getAttribute('data-fragment-name');
  }

  get defaultView() {
    return this.getAttribute('default');
  }

  get defaultMode() {
    const mode = (this.getAttribute('default-mode') || 'eager').toLowerCase();
    return mode === 'lazy' ? 'lazy' : 'eager';
  }

  get activeWhenFragment() {
    return this.getAttribute('active-when-fragment');
  }

  get activeWhenValue() {
    return this.getAttribute('active-when-value');
  }

  get effectiveValue() {
    const fromHash = this.hashValue;
    if (fromHash !== undefined && fromHash !== null && fromHash !== '') {
      return fromHash;
    }
    const d = this.defaultView;
    return d || undefined;
  }

  isActiveInStack() {
    if (this.defaultMode === 'eager') return true;
    const parentFrag = this.activeWhenFragment;
    if (!parentFrag) return true;
    return this.hashMap[parentFrag] === this.activeWhenValue;
  }

  /**
   * Same policy as uri-router: seed defaults with replaceState only (#510).
   */
  ensureDefaultPersisted() {
    if (!this.defaultView) return;
    if (!this.fragment) return;
    const current = this.hashMap[this.fragment];
    if (current !== undefined && current !== null && current !== '') return;
    if (!this.isActiveInStack()) return;
    const prev = this.noHistory;
    this.noHistory = true;
    try {
      this.hashValue = this.defaultView;
    } finally {
      this.noHistory = prev;
    }
  }

  get routes() {
    const allRoutes = Array.from(this.children);
    return allRoutes.filter((child) => {
      if (child.getAttribute(this.dataFragmentName)) {
        return child;
      }
    });
  }

  /**
   * Mark the nav child matching effectiveValue as .active
   */
  activatePage() {
    const active = this.effectiveValue;
    this.routes.map((p) => {
      if (active !== undefined && p.getAttribute(this.dataFragmentName) === active) {
        p.classList.add('active');
      } else {
        p.classList.remove('active');
      }
    });
  }

  onHashChange() {
    this.ensureDefaultPersisted();
    const value = this.effectiveValue;
    if (value !== undefined) {
      this._syncingUri = true;
      this.setAttribute('uri', value);
      this._syncingUri = false;
    }
    this.activatePage();
  }

  /**
   * @param {Event} e
   */
  navigationClick(e) {
    e.preventDefault();
    this.setAttribute('uri', e.currentTarget.getAttribute(this.dataFragmentName));
  }
}

export { UriRouterNavigationElement };
